/**
 * Schedule Lines Table with full feature parity to PO Lines.
 *
 * Supports sortable columns, filter panel, column show/hide with
 * localStorage persistence, CSV export, status badges, tab counts,
 * select-all / bulk actions, and per-row collaboration actions.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

import { purchasingAPI } from '../../../../lib/api';
import type { POLineSchedule } from '../../../../lib/api/types/purchasing';
import { useView } from '../../../../contexts/ViewContext';
import { useFormatters } from '../../../../contexts/PreferencesContext';
import { useAuth } from '../../../../contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../../ui/tabs';
import { Pagination } from '../../../ui/pagination';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Loader2,
  Search,
  CalendarRange,
  Columns,
  Download,
  RefreshCw,
  Eye,
  EyeOff,
  GripVertical,
  Check,
  X,
  Pause,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  ScheduleColumnConfig,
  DEFAULT_SCHEDULE_COLUMNS,
  SCHEDULE_COLUMN_STORAGE_KEY,
  ScheduleFilters,
  DEFAULT_SCHEDULE_FILTERS,
  ScheduleTab,
} from './types';
import { ScheduleFiltersPanel } from './ScheduleFiltersPanel';
import { ScheduleLineRow } from './ScheduleLineRow';
import {
  AcceptScheduleModal,
  DeclineScheduleModal,
  ProposeScheduleDateModal,
  ProposeScheduleQtyModal,
  HoldScheduleModal,
  ReleaseScheduleModal,
  BulkAcceptModal,
  BulkRejectModal,
  BulkHoldModal,
} from './ScheduleModals';
import type { ModalType, ScheduleActionPayload } from './ScheduleModals';
import { useScheduleBulkActions } from './useScheduleBulkActions';
import ScheduleDetailModal from '../../ScheduleDetailModal';

const PAGE_SIZE = 25;

// Every acknowledge_status choice from PurchaseOrderLine.ACKNOWLEDGE_STATUS_CHOICES
// except 'closed'. Used by the Due This/Next Week tabs to show the full
// week's plan without the terminal CR-3410 collaboration closures.
const ACK_STATUSES_NOT_CLOSED = [
  'new',
  'declined',
  'change_proposed_supplier',
  'wip',
  'accepted',
  'change_proposed_customer',
  'despatched',
  'change_declined',
  'cancelled',
  'queried',
].join(',');

// ----- Column persistence -----

function loadColumns(): ScheduleColumnConfig[] {
  try {
    const stored = localStorage.getItem(SCHEDULE_COLUMN_STORAGE_KEY);
    if (!stored) return DEFAULT_SCHEDULE_COLUMNS;
    const parsed = JSON.parse(stored) as ScheduleColumnConfig[];
    const storedMap = new Map(parsed.map((c) => [c.id, c]));
    return DEFAULT_SCHEDULE_COLUMNS.map((def) => {
      const saved = storedMap.get(def.id);
      return saved ? { ...def, visible: saved.visible, order: saved.order } : def;
    }).sort((a, b) => a.order - b.order);
  } catch {
    return DEFAULT_SCHEDULE_COLUMNS;
  }
}

function saveColumns(cols: ScheduleColumnConfig[]) {
  localStorage.setItem(SCHEDULE_COLUMN_STORAGE_KEY, JSON.stringify(cols));
}

function getSortIcon(field: string, sortField: string | null, sortDir: 'asc' | 'desc') {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-muted-foreground" />;
  return sortDir === 'asc'
    ? <ArrowUp className="h-3 w-3 text-primary" />
    : <ArrowDown className="h-3 w-3 text-primary" />;
}

// ----- CSV export is server-side (see handleExport in component) -----

export default function ScheduleLinesTable() {
  const { getViewQueryParams, isLoading: viewContextLoading } = useView();
  const { formatDate, formatNumber } = useFormatters();
  const { user } = useAuth();

  const isSupplier = user?.platform === 'SUPPLIER';

  const [schedules, setSchedules] = useState<POLineSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [needsActionCount, setNeedsActionCount] = useState<number | null>(null);
  const [dueThisWeekCount, setDueThisWeekCount] = useState<number | null>(null);
  const [dueNextWeekCount, setDueNextWeekCount] = useState<number | null>(null);
  const [openCount, setOpenCount] = useState<number | null>(null);
  const [onHoldCount, setOnHoldCount] = useState<number | null>(null);
  const [forecastedCount, setForecastedCount] = useState<number | null>(null);

  const getThisWeekRange = useCallback(() => {
    const today = new Date();
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysSinceMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(monday), to: fmt(sunday) };
  }, []);

  const getNextWeekRange = useCallback(() => {
    const today = new Date();
    const daysSinceMonday = (today.getDay() + 6) % 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() - daysSinceMonday + 7);
    nextMonday.setHours(0, 0, 0, 0);
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { from: fmt(nextMonday), to: fmt(nextSunday) };
  }, []);

  const [activeTab, setActiveTab] = useState<ScheduleTab>(isSupplier ? 'open' : 'due_this_week');
  const [sortField, setSortField] = useState<string | null>('schedule_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ScheduleFilters>(DEFAULT_SCHEDULE_FILTERS);

  const [columns, setColumns] = useState<ScheduleColumnConfig[]>(loadColumns);
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);

  const [modal, setModal] = useState<ModalType>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Close column dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(e.target as Node)) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ----- Data fetching -----

  const buildParams = useCallback((tab: ScheduleTab, page: number) => {
    const params: Record<string, string | number | boolean> = {
      page, page_size: PAGE_SIZE, ...getViewQueryParams(),
    };
    if (sortField) {
      // Map frontend column IDs to backend ordering fields
      const SORT_MAP: Record<string, string> = {
        schedule_line_number: 'schedule_line_number_int',
        line_number: 'po_line__line_number',
        po_number: 'po_line__po__po_number',
        part_number: 'po_line__part_number',
        supplier_name: 'po_line__po__supplier__name',
      };
      const backendField = SORT_MAP[sortField] || sortField;
      params['ordering'] = sortDirection === 'desc' ? `-${backendField}` : backendField;
    }
    if (tab === 'needs_action') params['needs_action'] = true;
    // Due This Week = Mon-Sun of current ISO week, OPEN rows only
    // (scheduled / partially_shipped). Already-shipped past days
    // would otherwise dominate the count and mean "history", not
    // "to do". Arrears from earlier in the week remain visible.
    // Skip closed-ack and on-hold rows.
    if (tab === 'due_this_week') {
      const { from, to } = getThisWeekRange();
      params['schedule_date__gte'] = from;
      params['schedule_date__lte'] = to;
      params['status__in'] = 'scheduled,partially_shipped';
      params['on_hold'] = false;
      params['acknowledge_status__in'] = ACK_STATUSES_NOT_CLOSED;
    }
    // Due Next Week = full Mon-Sun of next week, ANY status except
    // cancelled / closed-ack / on-hold. Shipped rows here read as
    // "received ahead of schedule" since the window is entirely
    // future, so they belong in the plan view.
    if (tab === 'due_next_week') {
      const { from, to } = getNextWeekRange();
      params['schedule_date__gte'] = from;
      params['schedule_date__lte'] = to;
      params['on_hold'] = false;
      params['acknowledge_status__in'] = ACK_STATUSES_NOT_CLOSED;
    }
    if (tab === 'open') {
      params['status__in'] = 'scheduled,partially_shipped';
      params['is_forecast'] = false;
      params['outstanding'] = true;
    }
    if (tab === 'closed') params['status'] = 'shipped';
    if (tab === 'on_hold') params['on_hold'] = true;
    if (tab === 'forecasted') params['is_forecast'] = true;
    if (filters.poNumber) params['search'] = filters.poNumber;
    if (filters.lineNumber) params['po_line__line_number'] = filters.lineNumber;
    if (filters.scheduleLineNumber) params['schedule_line_number'] = filters.scheduleLineNumber;
    if (filters.partNumber) params['po_line__part_number__icontains'] = filters.partNumber;
    if (filters.supplier) params['po_line__po__supplier__name__icontains'] = filters.supplier;
    if (filters.warehouse) params['warehouse'] = filters.warehouse;
    if (filters.acknowledgeStatus === 'forecast') {
      // Pseudo-status: filter to only the firm-boundary-derived forecast rows.
      params['is_forecast'] = true;
    } else if (filters.acknowledgeStatus) {
      params['acknowledge_status'] = filters.acknowledgeStatus;
    }
    if (filters.scheduleStatus) params['status'] = filters.scheduleStatus;
    if (filters.onHold === 'yes') params['on_hold'] = true;
    if (filters.onHold === 'no') params['on_hold'] = false;
    if (filters.scheduleDateFrom) params['schedule_date__gte'] = filters.scheduleDateFrom;
    if (filters.scheduleDateTo) params['schedule_date__lte'] = filters.scheduleDateTo;
    if (filters.promiseDateFrom) params['supplier_promise_date__gte'] = filters.promiseDateFrom;
    if (filters.promiseDateTo) params['supplier_promise_date__lte'] = filters.promiseDateTo;
    return params;
  }, [getViewQueryParams, sortField, sortDirection, filters, getThisWeekRange]);

  const handleExport = useCallback(async () => {
    // Server-side CSV — streams every row matching the current tab +
    // panel filters, bypassing UI pagination so users don't end up
    // with just the 25 rows currently on screen.
    try {
      const exportParams = { ...buildParams(activeTab, 1) };
      delete exportParams['page'];
      delete exportParams['page_size'];
      const qs = new URLSearchParams(
        Object.entries(exportParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const apiClientModule = await import('../../../../lib/api/client');
      const resp = await apiClientModule.default.get(
        `/purchasing/schedules/export-csv/?${qs}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `schedule_lines_${activeTab}_${today}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('Schedule CSV export failed:', err);
      toast.error('Failed to export CSV');
    }
  }, [buildParams, activeTab]);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await purchasingAPI.getSchedules(buildParams(activeTab, currentPage));
      setSchedules(res.data.results);
      setTotalCount(res.data.count);
      setTotalPages(Math.ceil(res.data.count / PAGE_SIZE));
    } catch (err: unknown) {
      console.error('Failed to fetch schedules:', err);
      setSchedules([]);
    } finally { setLoading(false); }
  }, [activeTab, currentPage, buildParams]);

  const fetchTabCounts = useCallback(async () => {
    try {
      const base: Record<string, string | number | boolean> = { page: 1, page_size: 1, ...getViewQueryParams() };
      const thisWeek = getThisWeekRange();
      const nextWeek = getNextWeekRange();
      const [na, dtw, dnw, op, hd, fc] = await Promise.all([
        purchasingAPI.getSchedules({ ...base, needs_action: true }),
        purchasingAPI.getSchedules({ ...base, schedule_date__gte: thisWeek.from, schedule_date__lte: thisWeek.to, status__in: 'scheduled,partially_shipped', on_hold: false, acknowledge_status__in: ACK_STATUSES_NOT_CLOSED }),
        purchasingAPI.getSchedules({ ...base, schedule_date__gte: nextWeek.from, schedule_date__lte: nextWeek.to, on_hold: false, acknowledge_status__in: ACK_STATUSES_NOT_CLOSED }),
        purchasingAPI.getSchedules({ ...base, status__in: 'scheduled,partially_shipped', is_forecast: false, outstanding: true }),
        purchasingAPI.getSchedules({ ...base, on_hold: true }),
        purchasingAPI.getSchedules({ ...base, is_forecast: true }),
      ]);
      setNeedsActionCount(na.data.count);
      setDueThisWeekCount(dtw.data.count);
      setDueNextWeekCount(dnw.data.count);
      setOpenCount(op.data.count);
      setOnHoldCount(hd.data.count);
      setForecastedCount(fc.data.count);
    } catch (err: unknown) { console.error('Failed to fetch tab counts:', err); }
  }, [getViewQueryParams, getThisWeekRange, getNextWeekRange]);

  useEffect(() => {
    if (!viewContextLoading) { fetchSchedules(); fetchTabCounts(); }
  }, [viewContextLoading, fetchSchedules, fetchTabCounts]);

  const refresh = () => { fetchSchedules(); fetchTabCounts(); };

  // ----- Bulk actions -----

  const bulk = useScheduleBulkActions({ schedules, refresh });

  // ----- Handlers -----

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (key: keyof ScheduleFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };
  const handleSearch = () => setCurrentPage(1);
  const handleClearFilters = () => { setFilters(DEFAULT_SCHEDULE_FILTERS); setCurrentPage(1); };
  const handleTabChange = (v: string) => { setActiveTab(v as ScheduleTab); setCurrentPage(1); bulk.setSelectedIds(new Set()); };

  // Column management
  const toggleColumn = (id: string) => {
    setColumns((prev) => { const n = prev.map((c) => c.id === id ? { ...c, visible: !c.visible } : c); saveColumns(n); return n; });
  };
  const resetColumns = () => { setColumns(DEFAULT_SCHEDULE_COLUMNS); saveColumns(DEFAULT_SCHEDULE_COLUMNS); };
  const selectAllCols = () => { setColumns((p) => { const n = p.map((c) => ({ ...c, visible: true })); saveColumns(n); return n; }); };
  const reorderColumns = (from: number, to: number) => {
    setColumns((prev) => { const n = [...prev]; const [m] = n.splice(from, 1); n.splice(to, 0, m); const o = n.map((c, i) => ({ ...c, order: i })); saveColumns(o); return o; });
  };

  // Modal actions
  const openModal = (kind: string, schedule: POLineSchedule) => {
    setModal({ kind, schedule } as ModalType);
  };

  const runAction = async (fn: () => Promise<unknown>, successMsg: string, errorMsg: string) => {
    setModalLoading(true);
    try {
      await fn();
      toast.success(successMsg);
      setModal(null);
      refresh();
    } catch (err: unknown) {
      console.error(errorMsg, err);
      toast.error(errorMsg);
      throw err;
    } finally {
      setModalLoading(false);
    }
  };

  const handleAcceptSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.acceptScheduleDate(data.scheduleId, { comments: data.comments }),
      'Delivery date accepted',
      'Failed to accept delivery date',
    );
  };

  const handleDeclineSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.declineScheduleDate(data.scheduleId, { reason: data.reason || '', comments: data.comments }),
      'Delivery date declined',
      'Failed to decline delivery date',
    );
  };

  const handleProposeDateSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.proposeScheduleDate(data.scheduleId, { proposed_date: data.proposedDate || '', reason: data.reason || '', comments: data.comments }),
      'Alternative date proposed',
      'Failed to propose alternative date',
    );
  };

  const handleProposeQtySubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.proposeScheduleQty(data.scheduleId, { proposed_quantity: data.proposedQty || 0, reason: data.reason || '', comments: data.comments }),
      'Quantity change proposed',
      'Failed to propose quantity change',
    );
  };

  const handleHoldSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.putScheduleOnHold(data.scheduleId, { reason: data.reason || '' }),
      'Schedule placed on hold',
      'Failed to put schedule on hold',
    );
  };

  const handleReleaseSubmit = async (data: ScheduleActionPayload) => {
    await runAction(
      () => purchasingAPI.releaseScheduleHold(data.scheduleId),
      'Hold released',
      'Failed to release hold',
    );
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== '');
  const visibleCols = columns.filter((c) => c.visible);

  return (
    <div className="space-y-4">
      {showFilters && (
        <ScheduleFiltersPanel filters={filters} onFilterChange={handleFilterChange} onSearch={handleSearch} onClearFilters={handleClearFilters} />
      )}

      <Card>
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CalendarRange className="h-5 w-5" />PO Line Schedules
                  {!loading && <Badge variant="secondary" className="ml-2 text-xs">{totalCount} schedules</Badge>}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">Showing {schedules.length} of {totalCount.toLocaleString()} schedule lines</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showFilters ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><Search className="h-3 w-3" />Filters</button>
                <button onClick={() => handleExport()} disabled={loading} className="px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed" title="Export all rows matching current tab + filters (streamed server-side, not just the current page)"><Download className="h-3 w-3" />Export</button>
                <Button variant="outline" size="sm" onClick={refresh} disabled={loading}><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>

                {/* Column Visibility Dropdown */}
                <div className="relative" ref={columnDropdownRef}>
                  <button onClick={() => setShowColumnDropdown(!showColumnDropdown)} className={`px-3 py-1.5 rounded-lg border text-sm flex items-center gap-2 ${showColumnDropdown ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}><Columns className="h-3 w-3" />Columns</button>
                  {showColumnDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-border rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                      <div className="p-2 border-b flex items-center justify-between">
                        <span className="text-sm font-medium">Manage Columns</span>
                        <div className="flex items-center gap-2">
                          <button onClick={selectAllCols} className="text-xs text-primary hover:underline">Select All</button>
                          <span className="text-xs text-muted-foreground">|</span>
                          <button onClick={resetColumns} className="text-xs text-primary hover:underline">Reset</button>
                        </div>
                      </div>
                      <div className="px-2 py-1 text-xs text-muted-foreground border-b bg-muted/30">Drag to reorder - Click to show/hide</div>
                      <div className="p-2 space-y-1">
                        {columns.map((col, idx) => (
                          <div key={col.id} draggable
                            onDragStart={(e) => { e.dataTransfer.setData('text/plain', idx.toString()); e.currentTarget.classList.add('opacity-50'); }}
                            onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-primary/10'); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove('bg-primary/10')}
                            onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-primary/10'); const di = parseInt(e.dataTransfer.getData('text/plain'), 10); if (di !== idx) reorderColumns(di, idx); }}
                            className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-move group">
                            <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
                            <input type="checkbox" checked={col.visible} onChange={() => toggleColumn(col.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer" />
                            <span className="text-sm flex-1">{col.label}</span>
                            {col.visible ? <Eye className="h-3 w-3 text-muted-foreground" /> : <EyeOff className="h-3 w-3 text-muted-foreground" />}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <TabsList className="mt-3 justify-start">
              <TabsTrigger value="all">All Lines</TabsTrigger>
              <TabsTrigger value="needs_action" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                Exceptions{needsActionCount !== null && needsActionCount > 0 ? ` (${needsActionCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="due_this_week" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                Due This Week{dueThisWeekCount !== null && dueThisWeekCount > 0 ? ` (${dueThisWeekCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="due_next_week" className="data-[state=active]:bg-sky-500 data-[state=active]:text-white">
                Due Next Week{dueNextWeekCount !== null && dueNextWeekCount > 0 ? ` (${dueNextWeekCount.toLocaleString()})` : ''}
              </TabsTrigger>
              <TabsTrigger value="open">Open{openCount !== null && openCount > 0 ? ` (${openCount.toLocaleString()})` : ''}</TabsTrigger>
              <TabsTrigger value="on_hold">On Hold{onHoldCount !== null && onHoldCount > 0 ? ` (${onHoldCount.toLocaleString()})` : ''}</TabsTrigger>
              {!isSupplier && (
                <TabsTrigger value="forecasted" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  Forecasted{forecastedCount !== null && forecastedCount > 0 ? ` (${forecastedCount.toLocaleString()})` : ''}
                </TabsTrigger>
              )}
            </TabsList>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /><p className="mt-2 text-sm text-muted-foreground">Loading schedules...</p></div></div>
            ) : schedules.length === 0 ? (
              <div className="text-center py-12">
                <CalendarRange className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">{hasActiveFilters ? 'No schedules match your filters' : 'No schedule lines found'}</p>
                {hasActiveFilters && <button onClick={handleClearFilters} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>}
              </div>
            ) : (
              <>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={PAGE_SIZE} onPageChange={setCurrentPage} itemLabel="schedules" />

                {/* Bulk Action Bar */}
                <div className="mb-4 p-3 bg-muted/40 border border-border rounded-lg flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      {bulk.selectedIds.size > 0 ? `${bulk.selectedIds.size} line${bulk.selectedIds.size > 1 ? 's' : ''} selected` : 'No lines selected'}
                    </span>
                    {bulk.selectedIds.size > 0 && (<button onClick={() => bulk.setSelectedIds(new Set())} className="text-xs text-muted-foreground underline hover:text-foreground">Clear</button>)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => bulk.setShowBulkHoldModal(true)} disabled={bulk.selectedIds.size === 0 || bulk.bulkHolding} className={bulk.selectedIds.size > 0 ? 'border-orange-500 text-orange-700 hover:bg-orange-50' : 'opacity-60 cursor-not-allowed'}>
                      {bulk.bulkHolding ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Holding...</> : <><Pause className="h-4 w-4 mr-2" />Put On Hold All</>}
                    </Button>
                    <Button size="sm" onClick={() => bulk.setShowBulkAcceptModal(true)} disabled={bulk.selectedIds.size === 0 || bulk.bulkAccepting} className={bulk.selectedIds.size > 0 ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-200 text-green-600 cursor-not-allowed opacity-60'}>
                      {bulk.bulkAccepting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Accepting...</> : <><Check className="h-4 w-4 mr-2" />Accept All</>}
                    </Button>
                    <Button size="sm" onClick={() => bulk.setShowBulkRejectModal(true)} disabled={bulk.selectedIds.size === 0 || bulk.bulkRejecting} className={bulk.selectedIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-red-200 text-red-400 cursor-not-allowed opacity-60'}>
                      {bulk.bulkRejecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Rejecting...</> : <><X className="h-4 w-4 mr-2" />Reject All</>}
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)] mt-4">
                  <table className="w-full">
                    <thead className="sticky top-0 z-20">
                      <tr className="border-b bg-muted/80 backdrop-blur-sm">
                        <th className="text-center py-3 px-2 font-medium" style={{ minWidth: '50px' }}>
                          <input type="checkbox" checked={bulk.getSelectableSchedules().length > 0 && bulk.selectedIds.size === bulk.getSelectableSchedules().length} onChange={bulk.handleSelectAll} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary" title="Select all eligible schedules" />
                        </th>
                        {visibleCols.map((col) => {
                          const right = ['schedule_qty', 'shipped_qty', 'balance_qty', 'unit_price'].includes(col.id);
                          const center = ['on_hold', 'waiting_on', 'line_number', 'schedule_line_number', 'action', 'unit_of_measure'].includes(col.id);
                          const align = right ? 'text-right' : center ? 'text-center' : 'text-left';
                          const justify = center ? 'justify-center' : right ? 'justify-end' : '';
                          return (
                            <th key={col.id} className={`${align} py-3 px-4 font-medium ${col.sortable ? 'cursor-pointer hover:bg-muted/50' : ''}`} style={{ minWidth: col.minWidth }} onClick={col.sortable ? () => handleSort(col.id) : undefined}>
                              {col.sortable ? <div className={`flex items-center gap-1 ${justify}`}>{col.label} {getSortIcon(col.id, sortField, sortDirection)}</div> : col.label}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((s) => (
                        <ScheduleLineRow
                          key={s.id}
                          schedule={s}
                          visibleColumns={visibleCols}
                          formatDate={formatDate}
                          formatNumber={formatNumber}
                          onOpenModal={openModal}
                          onRowClick={(id) => setSelectedScheduleId(id)}
                          isSelected={bulk.selectedIds.has(s.id)}
                          canSelectRow={bulk.canSelect(s)}
                          onToggleSelect={bulk.handleToggleSelect}
                          isSupplier={isSupplier}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} itemsPerPage={PAGE_SIZE} onPageChange={setCurrentPage} itemLabel="schedules" />
              </>
            )}
          </CardContent>
        </Tabs>
      </Card>

      <AcceptScheduleModal
        isOpen={modal?.kind === 'accept'}
        schedule={modal?.kind === 'accept' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleAcceptSubmit}
      />
      <DeclineScheduleModal
        isOpen={modal?.kind === 'decline'}
        schedule={modal?.kind === 'decline' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleDeclineSubmit}
      />
      <ProposeScheduleDateModal
        isOpen={modal?.kind === 'propose_date'}
        schedule={modal?.kind === 'propose_date' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleProposeDateSubmit}
      />
      <ProposeScheduleQtyModal
        isOpen={modal?.kind === 'propose_qty'}
        schedule={modal?.kind === 'propose_qty' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleProposeQtySubmit}
      />
      <HoldScheduleModal
        isOpen={modal?.kind === 'hold'}
        schedule={modal?.kind === 'hold' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleHoldSubmit}
      />
      <ReleaseScheduleModal
        isOpen={modal?.kind === 'release'}
        schedule={modal?.kind === 'release' ? modal.schedule : null}
        onClose={() => setModal(null)}
        submitting={modalLoading}
        onSubmit={handleReleaseSubmit}
      />

      <BulkAcceptModal open={bulk.showBulkAcceptModal} count={bulk.selectedIds.size} loading={bulk.bulkAccepting} onClose={() => bulk.setShowBulkAcceptModal(false)} onConfirm={bulk.handleBulkAccept} />
      <BulkRejectModal open={bulk.showBulkRejectModal} count={bulk.selectedIds.size} loading={bulk.bulkRejecting} onClose={() => bulk.setShowBulkRejectModal(false)} onConfirm={bulk.handleBulkReject} />
      <BulkHoldModal open={bulk.showBulkHoldModal} count={bulk.selectedIds.size} loading={bulk.bulkHolding} onClose={() => bulk.setShowBulkHoldModal(false)} onConfirm={bulk.handleBulkPutOnHold} />

      {selectedScheduleId !== null && (
        <ScheduleDetailModal
          isOpen={true}
          onClose={() => setSelectedScheduleId(null)}
          scheduleId={selectedScheduleId}
          onUpdate={refresh}
        />
      )}
    </div>
  );
}
